/**
 * PermissionModal Helpers
 * 권한 생성 모달 관련 상수 및 헬퍼 함수
 */
import { NAV_ITEMS, NavItem, NavSubItem } from "@/features/dashboard/config/navigation.config";

// ======== 타입 ========

export interface PermissionFormData {
    prmCode: string;
    prmName: string;
    prmDesc: string;
    baseUserType: string;
    accessPages: string[];
}

export interface PermissionModalProps {
    onClose: () => void;
    onCreate: (data: PermissionFormData) => Promise<void>;
}

export interface PageOption {
    id: string;
    label: string;
    page: string;
    isParent: boolean;
    parentId?: string;
    children?: string[];
}

// ======== 상수 ========

export const MODAL_TITLES = {
    CREATE: '권한 등록',
} as const;

export const FORM_LABELS = {
    PRM_CODE: '권한코드',
    PRM_NAME: '권한명',
    PRM_DESC: '권한설명',
    BASE_USER_TYPE: '기반 사용자 유형',
    ACCESS_PAGES: '접근페이지',
} as const;

/** 기반 사용자 유형 옵션 */
export const BASE_USER_TYPE_OPTIONS = [
    { value: 'C', label: '본사' },
    { value: 'S', label: '협력사' },
    { value: 'R', label: '라이더' },
] as const;

export const FORM_PLACEHOLDERS = {
    PRM_CODE: '권한코드를 입력하세요 (예: CUSTOM1)',
    PRM_NAME: '권한명을 입력하세요',
    PRM_DESC: '권한에 대한 설명을 입력하세요',
    ACCESS_PAGES: '접근 가능한 페이지를 선택하세요',
} as const;

export const BUTTON_LABELS = {
    CANCEL: '취소',
    SAVE: '저장',
    SAVING: '저장 중...',
} as const;

export const TOAST_MESSAGES = {
    REQUIRED_FIELDS: '필수 항목을 입력해주세요.',
    SUCCESS: '권한이 등록되었습니다.',
    ERROR: '권한 등록에 실패했습니다.',
} as const;

// ======== 초기값 ========

export const INITIAL_FORM_DATA: PermissionFormData = {
    prmCode: '',
    prmName: '',
    prmDesc: '',
    baseUserType: '',
    accessPages: [],
};

// ======== 헬퍼 함수 ========

/**
 * 서브메뉴에서 모든 페이지 코드 추출 (3단계 메뉴 포함)
 */
const extractPagesFromSubmenu = (submenu: NavSubItem[]): string[] => {
    const pages: string[] = [];
    submenu.forEach(sub => {
        if (sub.page) {
            pages.push(sub.page);
        }
        // 3단계 메뉴 처리
        if (sub.children) {
            sub.children.forEach(child => {
                if (child.page) {
                    pages.push(child.page);
                }
            });
        }
    });
    return pages;
};

/**
 * NAV_ITEMS에서 페이지 옵션 목록 생성
 * 부모 메뉴 선택 시 하위 메뉴 모두 접근 가능
 * 3단계 메뉴도 지원
 */
export const buildPageOptions = (): PageOption[] => {
    const options: PageOption[] = [];

    NAV_ITEMS.forEach((navItem: NavItem) => {
        // 부모 메뉴 추가
        const childPages = navItem.submenu ? extractPagesFromSubmenu(navItem.submenu) : [];
        options.push({
            id: navItem.id,
            label: navItem.label,
            page: navItem.id,
            isParent: true,
            children: childPages,
        });

        // 하위 메뉴 추가
        navItem.submenu?.forEach((subItem: NavSubItem) => {
            // 3단계 메뉴가 있는 경우
            if (subItem.children) {
                // 3단계 부모 메뉴 추가
                const childPages = subItem.children
                    .map(child => child.page)
                    .filter((page): page is string => !!page);

                options.push({
                    id: subItem.id,
                    label: subItem.label,
                    page: subItem.id,
                    isParent: true,
                    parentId: navItem.id,
                    children: childPages,
                });

                // 3단계 하위 메뉴 추가
                subItem.children.forEach(child => {
                    if (child.page) {
                        options.push({
                            id: child.id,
                            label: child.label,
                            page: child.page,
                            isParent: false,
                            parentId: subItem.id,
                        });
                    }
                });
            } else if (subItem.page) {
                // 일반 서브메뉴 (페이지 링크)
                options.push({
                    id: subItem.id,
                    label: subItem.label,
                    page: subItem.page,
                    isParent: false,
                    parentId: navItem.id,
                });
            }
        });
    });

    return options;
};

/**
 * 선택된 페이지에서 실제 접근 가능한 페이지 목록 계산
 * 부모 선택 시 하위 페이지 모두 포함
 */
export const calculateAccessiblePages = (
    selectedPages: string[],
    pageOptions: PageOption[]
): string[] => {
    const accessiblePages = new Set<string>();

    selectedPages.forEach(selectedId => {
        const option = pageOptions.find(opt => opt.id === selectedId);
        if (!option) return;

        if (option.isParent && option.children) {
            // 부모 선택 시 모든 자식 페이지 추가
            option.children.forEach(childPage => accessiblePages.add(childPage));
        } else {
            // 하위 메뉴 선택 시 해당 페이지만 추가
            accessiblePages.add(option.page);
        }
    });

    return Array.from(accessiblePages);
};

/**
 * 폼 유효성 검사
 */
export const validateForm = (formData: PermissionFormData): boolean => {
    return !!(
        formData.prmCode.trim() &&
        formData.prmName.trim() &&
        formData.baseUserType.trim() &&
        formData.accessPages.length > 0
    );
};

/**
 * 그룹별 페이지 옵션 구조화
 */
export const getGroupedPageOptions = (): { parent: PageOption; children: PageOption[] }[] => {
    const pageOptions = buildPageOptions();
    const grouped: { parent: PageOption; children: PageOption[] }[] = [];

    pageOptions.forEach(option => {
        if (option.isParent) {
            const children = pageOptions.filter(
                opt => !opt.isParent && opt.parentId === option.id
            );
            grouped.push({ parent: option, children });
        }
    });
    return grouped;
};

/**
 * 페이지 코드 -> 한글 라벨 매핑 생성
 */
export const buildPageCodeToLabelMap = (): Record<string, string> => {
    const map: Record<string, string> = {};

    NAV_ITEMS.forEach((navItem: NavItem) => {
        // 부모 메뉴 매핑
        map[navItem.id] = navItem.label;

        // 하위 메뉴 매핑
        navItem.submenu?.forEach((subItem: NavSubItem) => {
            map[subItem.id] = subItem.label;
            if (subItem.page) {
                map[subItem.page] = subItem.label;
            }
            // 3단계 메뉴 매핑
            subItem.children?.forEach(child => {
                map[child.id] = child.label;
                if (child.page) {
                    map[child.page] = child.label;
                }
            });
        });
    });

    return map;
};

/**
 * 쉼표로 구분된 페이지 코드 문자열을 한글 라벨로 변환
 * @param pagesStr - 쉼표로 구분된 페이지 코드 문자열 (예: "dashboard, rider_status")
 * @returns 한글 라벨 문자열 (예: "대시보드, 라이더 현황")
 */
export const convertPageCodesToLabels = (pagesStr: string): string => {
    if (!pagesStr) return '';

    const labelMap = buildPageCodeToLabelMap();
    const codes = pagesStr.split(',').map(code => code.trim());

    const labels = codes
        .map(code => labelMap[code] || code)
        .filter((label, index, self) => self.indexOf(label) === index); // 중복 제거

    return labels.join(', ');
};
